import { useState } from "react";
import Navbar from "../Navbar/Navbar"; // dev: point this at your project's Navbar
import Footer from "../Footer/Footer"; // dev: point this at your project's Footer
import {
  Lightbulb,
  Flame,
  Octagon,
  Radio,
  HeartPulse,
  Shirt,
  Flag,
  TrafficCone,
  FileText,
  ShieldCheck,
  Search,
  UploadCloud,
  CircleCheck,
  Maximize2,
  X,
  ImageOff,
  Hand,
} from "lucide-react";

const WA = {
  navy: "#0d1f3c",
  navyDeep: "#0a1830",
  navyMid: "#1a3358",
  steel: "#1e56a8",
  slate: "#94a3b8",
  smoke: "#f8fafc",
  smokeDk: "#f1f5f9",
  gold: "#c9971c",
  goldDeep: "#9c761a",
  border: "#e2e8f0",
  textDark: "#0d1f3c",
  textSecondary: "#475569",
  textMuted: "#64748b",
  blueTint: "#dbeafe",
  goldTint: "#fef9ec",
};

// ---------------------------------------------------------------------------
// EQUIPMENT DATA
// `image` starts null. Clicking the upload button on a card (or in the
// detail modal) reads a local file with FileReader and stores it as a data
// URL in React state — no backend required. A developer can also hardcode a
// real photo URL here ahead of time if preferred.
// ---------------------------------------------------------------------------
const INITIAL_EQUIPMENT = [
  {
    key: "amber-light",
    icon: Lightbulb,
    title: "Amber rotating / strobe light",
    desc: "360\u00b0 visibility, visible 500 ft minimum. Required whenever escorting.",
    reg: "MUTCD 6F",
    image: null,
    specs: [
      "360\u00b0 rotating or strobe beacon",
      "Visible from at least 500 ft in normal daylight",
      "Mounted at the highest practical point on the vehicle",
      "Must remain active during ferry loading and unloading",
    ],
  },
  {
    key: "fire-extinguisher",
    icon: Flame,
    title: "Fire extinguisher",
    desc: "2.5 lb ABC minimum. Securely mounted and accessible.",
    reg: "NFPA 10",
    image: null,
    specs: [
      "2.5 lb ABC type minimum",
      "Fully charged with an unobstructed gauge",
      "Securely mounted and easy to reach",
      "Checked during every pre-trip inspection",
    ],
  },
  {
    key: "oversize-sign",
    icon: Octagon,
    title: "Oversize load sign",
    desc: '18" x 84" minimum. Yellow background with black retroreflective letters.',
    reg: "MUTCD 6F.56",
    image: null,
    specs: [
      '18" x 84" minimum size',
      "Yellow background, black retroreflective letters",
      "Legible from at least 500 ft",
      "Also required on the rear of loads over 14 ft wide",
    ],
  },
  {
    key: "cb-radio",
    icon: Radio,
    title: "CB radio",
    desc: "Required. Channel 19 default \u2014 maintain communication at all times.",
    reg: "FCC / DOT",
    image: null,
    specs: [
      "Channel 19 is the default escort channel",
      "Two-way contact with the carrier driver is mandatory",
      "Range-test with the carrier before departure",
    ],
  },
  {
    key: "stop-slow-paddle",
    icon: Hand,
    title: "Stop / slow paddle",
    desc: '18" diameter minimum. MUTCD compliant, used for flagging operations.',
    reg: "MUTCD 6E.03",
    image: null,
    specs: [
      '18" diameter minimum',
      "MUTCD compliant octagonal/rectangular faces",
      "Used for flagging and temporary traffic control",
      "Must be undamaged and fully retroreflective",
    ],
  },
  {
    key: "two-way-radio",
    icon: Radio,
    title: "Two-way radio",
    desc: "Required in Washington. Coordinates lead and rear escort positioning.",
    reg: "State DOT",
    image: null,
    specs: [
      "Required whenever lead and rear escorts are both assigned",
      "Confirm a shared channel before the move begins",
      "Used for intersection clearance and hazard calls",
    ],
  },
  {
    key: "first-aid-kit",
    icon: HeartPulse,
    title: "First aid kit",
    desc: "OSHA Class A minimum. Fully stocked and within expiration.",
    reg: "OSHA 1910.151",
    image: null,
    specs: [
      "OSHA Class A minimum contents",
      "Sealed and fully stocked",
      "Check expiration dates before every move",
    ],
  },
  {
    key: "hi-vis-vest",
    icon: Shirt,
    title: "High-visibility vest",
    desc: "ANSI/ISEA 107 Class 2 minimum; Class 3 required on roadway and ferry decks.",
    reg: "OSHA 1910.132",
    image: null,
    specs: [
      "ANSI/ISEA 107 Class 2 minimum inside the vehicle",
      "Class 3 required whenever outside the vehicle on a roadway",
      "Class 3 required on all Washington State Ferry decks",
    ],
  },
  {
    key: "flags",
    icon: Flag,
    title: "Red / orange flags",
    desc: '18" x 18" minimum. Spare flags required to re-flag the load.',
    reg: "MUTCD 6E.01",
    image: null,
    specs: [
      '18" x 18" minimum size',
      "Mounted on the front and rear corners of the load",
      "Carry spares in case of loss or damage",
    ],
  },
  {
    key: "traffic-control",
    icon: TrafficCone,
    title: "Traffic control devices",
    desc: "Cones or triangles as required by permit conditions.",
    reg: "DOT 393.95",
    image: null,
    specs: [
      "Cones or warning triangles",
      "Used for breakdowns or temporary traffic control",
      "Quantity and placement vary by permit conditions",
    ],
  },
  {
    key: "permit-documents",
    icon: FileText,
    title: "Permit & documents",
    desc: "Permit, route plan, insurance, and key contact information.",
    reg: "WSDOT",
    image: null,
    specs: [
      "WSDOT Pilot Car Operator Permit card",
      "Route plan and proof of insurance",
      "Key emergency and agency contact numbers",
    ],
  },
];

const COMPLIANCE_ITEMS = [
  "Carry all required equipment at all times during a permitted move.",
  "Equipment must be kept in good working order at all times.",
  "WSDOT or WSP inspection may be conducted at any time.",
  "Non-compliance may result in fines or permit revocation.",
];

const STATE_GROUPS = [
  { name: "14 PEVO states", badge: "PEVO", sub: "Performance-based", states: "AL, AR, CO, CT, DE, FL, GA, KY, LA, MI, MS, NC, NM, SC", highlight: false },
  { name: "8-state group", badge: "Uniform", sub: "Uniform requirements", states: "AZ, ID, IA, KS, MO, NE, NV, UT", highlight: true },
  { name: "Stand-alone states", badge: "Independent", sub: "Independent requirements", states: "CA, OR, TN, TX, WI, WY", highlight: false },
  { name: "Remaining states", badge: "21 states", sub: "To complete all 50", states: "AK, DC, HI, IL, IN, MD, MA, ME, MN, MT, ND, OH, OK, PA, RI, SD, VA, VT, WV, NY, NJ, NH", highlight: false },
];

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: WA.steel }} />
      <h2 className="text-xs font-bold tracking-widest uppercase" style={{ color: WA.navy }}>
        {children}
      </h2>
    </div>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Real working upload control: a label wraps a hidden file input, so
// clicking the visible circular icon opens the OS file picker. No refs,
// no extra wiring — this is the part that was missing before.
function UploadButton({ itemKey, onFile, size = 28, className = "" }) {
  const inputId = "upload-input-" + itemKey;
  return (
    <label
      htmlFor={inputId}
      title="Upload photo"
      className={"inline-flex items-center justify-center rounded-full cursor-pointer transition-colors hover:brightness-95 " + className}
      style={{ width: size, height: size, backgroundColor: WA.blueTint, border: "1px solid " + WA.steel }}
    >
      <UploadCloud size={Math.round(size * 0.5)} style={{ color: WA.steel }} strokeWidth={2.2} />
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          const dataUrl = await readFileAsDataUrl(file);
          onFile(dataUrl);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function ImagePlaceholder({ itemKey }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-1.5 rounded-xl p-6"
      style={{ backgroundColor: WA.smokeDk, border: "1.5px dashed " + WA.slate }}
    >
      <ImageOff size={24} style={{ color: WA.slate }} strokeWidth={1.8} />
      <div className="text-[10px] font-bold uppercase tracking-wide mt-1" style={{ color: WA.textMuted }}>
        No photo yet
      </div>
      <div className="text-[11px] text-center leading-snug" style={{ color: WA.textMuted }}>
        Use the upload button to add a photo for &ldquo;{itemKey}&rdquo;
      </div>
    </div>
  );
}

function EquipmentCard({ item, onEnlarge, onUploadImage }) {
  const Icon = item.icon;
  return (
    <div
      className="relative flex items-start gap-4 rounded-2xl p-5 bg-white"
      style={{ border: "1px solid " + WA.border, boxShadow: "0 1px 2px rgba(47,58,86,0.05), 0 6px 16px rgba(47,58,86,0.07)" }}
    >
      {item.image ? (
        <img src={item.image} alt={item.title} className="flex-shrink-0 w-11 h-11 rounded-xl object-cover" />
      ) : (
        <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: WA.blueTint }}>
          <Icon size={22} style={{ color: WA.steel }} strokeWidth={1.8} />
        </div>
      )}

      <div className="flex-1 pr-14">
        <h3 className="text-sm font-bold mb-1" style={{ color: WA.navy }}>{item.title}</h3>
        <p className="text-xs leading-relaxed" style={{ color: WA.textSecondary }}>{item.desc}</p>
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        <UploadButton itemKey={item.key} onFile={onUploadImage} size={26} />
        <button
          type="button"
          aria-label={"Enlarge " + item.title}
          onClick={() => onEnlarge(item)}
          className="inline-flex items-center justify-center rounded-full transition-colors hover:brightness-95"
          style={{ width: 26, height: 26, backgroundColor: WA.goldTint, border: "1px solid " + WA.gold }}
        >
          <Maximize2 size={13} style={{ color: WA.goldDeep }} strokeWidth={2.3} />
        </button>
      </div>
    </div>
  );
}

function ChecklistRow({ item, checked, onToggle, onEnlarge, onUploadImage }) {
  const Icon = item.icon;
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-4 py-3 bg-white"
      style={{ border: "1px solid " + WA.border, boxShadow: "0 1px 2px rgba(47,58,86,0.04)" }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={(checked ? "Unmark" : "Mark") + " " + item.title + " as verified"}
        onClick={() => onToggle(item.key)}
        className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors"
        style={{
          backgroundColor: checked ? WA.gold : "#fff",
          border: "1.5px solid " + (checked ? WA.goldDeep : WA.slate),
        }}
      >
        {checked && <CircleCheck size={13} style={{ color: WA.navyDeep }} strokeWidth={3} />}
      </button>

      {item.image ? (
        <img src={item.image} alt={item.title} className="flex-shrink-0 w-9 h-9 rounded-lg object-cover" />
      ) : (
        <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: WA.blueTint }}>
          <Icon size={18} style={{ color: WA.steel }} strokeWidth={1.8} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <h3
          className="text-[13px] font-bold truncate"
          style={{ color: checked ? WA.textMuted : WA.navy, textDecoration: checked ? "line-through" : "none" }}
        >
          {item.title}
        </h3>
        <p className="text-[11.5px] leading-snug truncate" style={{ color: WA.textSecondary }}>{item.desc}</p>
      </div>

      <span
        className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1"
        style={{ backgroundColor: WA.smokeDk, color: WA.textSecondary }}
      >
        {item.reg}
      </span>

      <div className="flex-shrink-0 flex items-center gap-1.5">
        <UploadButton itemKey={item.key} onFile={onUploadImage} size={24} />
        <button
          type="button"
          aria-label={"Enlarge " + item.title}
          onClick={() => onEnlarge(item)}
          className="inline-flex items-center justify-center rounded-full transition-colors hover:brightness-95"
          style={{ width: 24, height: 24, backgroundColor: WA.goldTint, border: "1px solid " + WA.gold }}
        >
          <Maximize2 size={12} style={{ color: WA.goldDeep }} strokeWidth={2.3} />
        </button>
      </div>
    </div>
  );
}

function DetailModal({ item, onClose, onUploadImage }) {
  if (!item) return null;
  const Icon = item.icon;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ backgroundColor: "rgba(35,44,66,0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 relative"
        style={{ boxShadow: "0 4px 10px rgba(47,58,86,0.08), 0 20px 40px rgba(47,58,86,0.12)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:brightness-95"
          style={{ backgroundColor: WA.smoke }}
        >
          <X size={16} style={{ color: WA.textSecondary }} />
        </button>

        <div className="mb-5 relative">
          {item.image ? (
            <img src={item.image} alt={item.title} className="w-full h-48 object-cover rounded-xl" />
          ) : (
            <ImagePlaceholder itemKey={item.key} />
          )}
          <div className="absolute bottom-3 right-3">
            <UploadButton itemKey={item.key + "-modal"} onFile={onUploadImage} size={34} />
          </div>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: WA.blueTint }}>
            <Icon size={19} style={{ color: WA.steel }} strokeWidth={1.9} />
          </div>
          <h3 className="text-base font-bold" style={{ color: WA.navy }}>{item.title}</h3>
        </div>

        <p className="text-sm leading-relaxed mb-4" style={{ color: WA.textSecondary }}>{item.desc}</p>

        <div className="flex flex-col gap-2.5">
          {item.specs.map((spec, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CircleCheck size={15} style={{ color: WA.goldDeep }} className="flex-shrink-0 mt-0.5" strokeWidth={2.2} />
              <p className="text-xs leading-relaxed" style={{ color: WA.textDark }}>{spec}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function EquipmentInfographic() {
  const [items, setItems] = useState(INITIAL_EQUIPMENT);
  const [activeKey, setActiveKey] = useState(null);
  const [pressed, setPressed] = useState(false);
  const [checked, setChecked] = useState({});

  const activeItem = items.find((i) => i.key === activeKey) || null;
  const verifiedCount = Object.values(checked).filter(Boolean).length;
  const totalCount = items.length;

  function toggleChecked(key) {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleUploadImage(key, dataUrl) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, image: dataUrl } : it)));
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen w-full p-6 sm:p-10" style={{ backgroundColor: WA.smoke, fontFamily: "-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" }}>
      <div className="max-w-5xl mx-auto flex flex-col gap-6">

        <SectionLabel>Quick-reference infographic</SectionLabel>

        <div className="relative">

          <div
            className="rounded-3xl p-8 sm:p-10 flex items-center justify-between gap-8 flex-wrap"
            style={{ backgroundColor: WA.navy, boxShadow: "0 4px 10px rgba(47,58,86,0.08), 0 20px 40px rgba(47,58,86,0.12)" }}
          >
            <div className="max-w-xl">
              <span
                className="inline-block text-xs font-bold uppercase tracking-wider rounded-full px-3.5 py-1.5 mb-4"
                style={{ backgroundColor: WA.gold, color: WA.navyDeep }}
              >
                Required equipment checklist
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight text-white">Checklist &mdash; Washington</h1>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: "#CBD6E6" }}>
                All equipment must be in good working condition and clearly visible or accessible. Verify
                requirements with your permit before each move.
              </p>
            </div>
            <div
              className="flex-shrink-0 w-24 h-24 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: WA.gold, boxShadow: "0 8px 18px rgba(0,0,0,0.28)" }}
            >
              <span className="text-2xl font-bold" style={{ color: WA.navyDeep }}>WA</span>
            </div>
          </div>

          <div className="mt-6">
            <SectionLabel>Equipment checklist</SectionLabel>

            <div
              className="rounded-2xl bg-white p-5 mb-4"
              style={{ border: "1px solid " + WA.border, boxShadow: "0 1px 2px rgba(47,58,86,0.05)" }}
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: WA.textMuted }}>
                  {totalCount}-item pre-trip checklist &middot; Washington
                </span>
                <span className="text-[12px] font-bold" style={{ color: WA.navy }}>
                  {verifiedCount}/{totalCount} verified
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: WA.smokeDk }}>
                <div
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: (totalCount ? (verifiedCount / totalCount) * 100 : 0) + "%",
                    backgroundColor: WA.gold,
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {items.map((item) => (
                <ChecklistRow
                  key={item.key}
                  item={item}
                  checked={!!checked[item.key]}
                  onToggle={toggleChecked}
                  onEnlarge={(it) => setActiveKey(it.key)}
                  onUploadImage={(dataUrl) => handleUploadImage(item.key, dataUrl)}
                />
              ))}
            </div>
          </div>

          <div className="mt-6">
            <SectionLabel>Compliance &amp; classification</SectionLabel>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-4 items-stretch">

              <div
                className="rounded-2xl p-6 flex flex-col gap-4 text-white"
                style={{ backgroundColor: WA.navy, boxShadow: "0 4px 10px rgba(47,58,86,0.08), 0 20px 40px rgba(47,58,86,0.12)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(232,194,122,0.18)" }}>
                    <ShieldCheck size={18} style={{ color: WA.gold }} strokeWidth={1.9} />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: WA.gold }}>
                    Important &mdash; stay compliant
                  </h3>
                </div>
                <div className="flex flex-col gap-3">
                  {COMPLIANCE_ITEMS.map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CircleCheck size={16} style={{ color: WA.gold }} className="flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="text-sm leading-relaxed" style={{ color: "#E4ECF3" }}>{text}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                  {[
                    ["Width", "14'6\""],
                    ["Height", "14'6\""],
                    ["Insurance", "$750K CSL"],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div className="text-[9px] font-bold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.5)" }}>{label}</div>
                      <div className="text-sm font-bold mt-0.5" style={{ color: WA.gold }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="rounded-2xl p-6 flex flex-col gap-4 bg-white"
                style={{ border: "1px solid " + WA.border, boxShadow: "0 1px 2px rgba(47,58,86,0.05), 0 6px 16px rgba(47,58,86,0.07)" }}
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-bold" style={{ color: WA.navy }}>PPCA state certification groups</h3>
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: WA.textMuted }}>National reference</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {STATE_GROUPS.map((g, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-3.5"
                      style={{
                        backgroundColor: g.highlight ? WA.goldTint : WA.smoke,
                        border: g.highlight ? "1.5px solid " + WA.gold : "1px solid " + WA.border,
                      }}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <h4 className="text-xs font-bold" style={{ color: WA.navy }}>{g.name}</h4>
                        <span
                          className="text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                          style={{
                            backgroundColor: g.highlight ? WA.gold : WA.steel,
                            color: g.highlight ? WA.navyDeep : "#fff",
                          }}
                        >
                          {g.badge}
                        </span>
                      </div>
                      <div className="text-[10px] italic font-semibold mb-1.5" style={{ color: WA.textMuted }}>{g.sub}</div>
                      <div className="text-[11px] leading-relaxed" style={{ color: WA.textSecondary }}>{g.states}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          <div
            className="mt-6 rounded-2xl px-6 py-4 flex items-center justify-between flex-wrap gap-3 text-white"
            style={{ backgroundColor: WA.navyDeep, boxShadow: "0 1px 2px rgba(47,58,86,0.05), 0 6px 16px rgba(47,58,86,0.07)" }}
          >
            <div className="flex items-center gap-3 text-sm font-semibold">
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(232,194,122,0.18)" }}>
                <CircleCheck size={14} style={{ color: WA.gold }} strokeWidth={2} />
              </div>
              Always verify current requirements with <b style={{ color: WA.gold }}>&nbsp;WSDOT&nbsp;</b> and your permit before each move.
            </div>
            <div className="text-xs" style={{ color: "#C7D2DC" }}>wsdot.wa.gov &nbsp;|&nbsp; 1-360-705-7000</div>
          </div>

          <div className="absolute -bottom-7 right-10 flex items-center gap-3">
            <span
              className="text-xs font-bold rounded-full px-4 py-2 text-white"
              style={{ backgroundColor: WA.navy, boxShadow: "0 1px 2px rgba(47,58,86,0.05), 0 6px 16px rgba(47,58,86,0.07)" }}
            >
              Enlarge
            </span>
            <button
              type="button"
              aria-label="Enlarge infographic"
              onMouseDown={() => setPressed(true)}
              onMouseUp={() => setPressed(false)}
              onMouseLeave={() => setPressed(false)}
              className="w-16 h-16 rounded-full flex items-center justify-center transition-transform active:scale-95"
              style={{
                backgroundColor: pressed ? WA.navyDeep : WA.navy,
                border: "3px solid " + WA.gold,
                boxShadow: pressed
                  ? "0 4px 10px rgba(35,44,66,0.45)"
                  : "0 10px 22px rgba(35,44,66,0.38), 0 3px 8px rgba(35,44,66,0.28)",
              }}
            >
              <Search size={28} color="#fff" strokeWidth={2.4} />
            </button>
          </div>

        </div>

        <div className="mt-10 rounded-2xl p-5 bg-white" style={{ border: "1.5px dashed " + WA.slate }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: WA.smokeDk }}>
              <UploadCloud size={18} style={{ color: WA.steel }} strokeWidth={2} />
            </div>
            <div className="text-xs font-bold uppercase tracking-wide" style={{ color: WA.textMuted }}>
              How the upload and enlarge buttons work
            </div>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: WA.textSecondary }}>
            Each equipment card has two buttons in its top-right corner: the blue upload icon opens your device's
            file picker and immediately previews the chosen photo on that card (stored in component state via
            FileReader — no backend required). The gold enlarge icon opens a detail modal for that item with a
            larger image area and its full spec list. If these don't respond in your project, confirm
            lucide-react is installed and that this file is rendered as a real React component, not pasted as
            static HTML.
          </p>
        </div>

      </div>

      <DetailModal
        item={activeItem}
        onClose={() => setActiveKey(null)}
        onUploadImage={(dataUrl) => activeItem && handleUploadImage(activeItem.key, dataUrl)}
      />
      </div>
      <Footer />
    </>
  );
}
